import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import api from '../services/api';
import { syncLanguageFromServer, resetLanguageOnLogout } from '../i18n';

const AppContext = createContext(null);

// Navigation items with role permissions
const navigationItems = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', roles: ['super_admin', 'admin', 'teacher', 'student', 'finance_admin'] },
  { id: 'parent-portal', label: 'My Children', path: '/parent-portal', roles: ['parent'] },
  { id: 'my-profile', label: 'My Profile', path: '/my-profile', roles: ['teacher'] },
  { id: 'attendance', label: 'Student Attendance', path: '/attendance', roles: ['admin', 'teacher'] },
  { id: 'learning', label: 'Learning', path: '/learning', roles: ['teacher', 'student'] },
  { id: 'students', label: 'Students', path: '/students', roles: ['admin', 'teacher'] },
  { id: 'teachers', label: 'Teachers', path: '/teachers', roles: ['admin'] },
  { id: 'leave-management', label: 'Leave Management', path: '/leave-management', roles: ['admin', 'teacher'] },
  { id: 'teacher-schedule', label: 'Teacher Schedule', path: '/teacher-schedule', roles: ['admin', 'teacher'] },
  { id: 'operations', label: 'Operations', path: '/operations', roles: ['admin'] },
  { id: 'finance', label: 'Finance', path: '/finance', roles: ['admin', 'finance_admin'] },
  { id: 'analytics', label: 'Analytics', path: '/analytics', roles: ['admin', 'teacher', 'finance_admin'] },
  { id: 'library', label: 'Library', path: '/library', roles: ['admin', 'teacher', 'student'] },
  { id: 'administration', label: 'Administration', path: '/administration', roles: ['admin'] },
  { id: 'school-management', label: 'School Management', path: '/school-management', roles: ['super_admin'] },
];

export function AppProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentSchool, setCurrentSchool] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  // Mobile-only state: drawer overlay open/closed. Persistent collapse vs.
  // ephemeral drawer must NOT share a flag — desktop users keep their pref,
  // mobile users get a transient overlay.
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [theme, setTheme] = useState('light');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Initialize auth state from localStorage
  useEffect(() => {
    const token = api.getToken();
    const user = api.getUser();
    const school = api.getSchool();

    if (token && user) {
      setCurrentUser(user);
      setCurrentSchool(school);
      setIsAuthenticated(true);
      // Apply this user's saved language preference (per-user, isolated from
      // any other account that may have logged in on this device before).
      syncLanguageFromServer(user.userId);
      // v96 — Re-hydrate school payload (esp. schoolLogo) once, so users on
      // pre-logo-feature sessions immediately pick up the branded sidebar
      // without needing to re-login.
      if (user.schoolId && user.schoolId !== 'system') {
        api.getCurrentUser().then((me) => {
          if (me?.school) {
            api.setSchool(me.school);
            setCurrentSchool(me.school);
          }
        }).catch(() => { /* silent — offline / expired token handled elsewhere */ });
      }

      // Fetch notifications
      fetchNotifications();
    }
    setIsLoading(false);
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const data = await api.getNotifications();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  // Login
  const login = useCallback(async (schoolCode, role, email, password) => {
    const data = await api.login(schoolCode, role, email, password);
    setCurrentUser(data.user);
    setCurrentSchool(data.school);
    setIsAuthenticated(true);
    syncLanguageFromServer(data.user?.userId);
    fetchNotifications();
    return data;
  }, [fetchNotifications]);

  // Individual (B2C) login — email + userCode + password
  const loginIndividual = useCallback(async (email, userCode, password, deviceId) => {
    const data = await api.loginIndividual(email, userCode, password, deviceId);
    setCurrentUser(data.user);
    setCurrentSchool(null);
    setIsAuthenticated(true);
    syncLanguageFromServer(data.user?.userId);
    return data;
  }, []);

  // Adopt a Google OAuth session that the backend already minted (the
  // browser came back from /api/auth/google/callback with the JWT in
  // the URL fragment). We persist it via api.adoptGoogleSession AND
  // flip React auth state so ProtectedRoute lets the user through on
  // the very next navigate().
  const loginWithGoogleSession = useCallback(async (token, user) => {
    await api.adoptGoogleSession(token, user);
    setCurrentUser(user);
    setCurrentSchool(null);
    setIsAuthenticated(true);
    syncLanguageFromServer(user?.userId);
    fetchNotifications();
    return { token, user };
  }, [fetchNotifications]);

  // Logout
  const logout = useCallback(() => {
    api.logout();
    setCurrentUser(null);
    setCurrentSchool(null);
    setIsAuthenticated(false);
    setNotifications([]);
    setUnreadCount(0);
    // Reset runtime UI language so the next person on this device sees the
    // neutral English default until they authenticate as themselves.
    resetLanguageOnLogout();
  }, []);

  // Auto-logout after 15 min of inactivity
  useEffect(() => {
    if (!isAuthenticated) return;
    let timer;
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { logout(); window.location.href = '/'; }, 15 * 60 * 1000);
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [isAuthenticated, logout]);

  // Toggle sidebar (desktop collapse vs. expand)
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  // Toggle the mobile drawer (<lg screens only)
  const toggleMobileDrawer = useCallback(() => {
    setMobileDrawerOpen(prev => !prev);
  }, []);
  const closeMobileDrawer = useCallback(() => setMobileDrawerOpen(false), []);

  // Toggle theme
  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const newTheme = prev === 'light' ? 'dark' : 'light';
      if (newTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return newTheme;
    });
  }, []);

  // Mark notification as read
  const markNotificationRead = useCallback(async (notificationId) => {
    try {
      await api.markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.notificationId === notificationId ? { ...n, readStatus: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllNotificationsRead = useCallback(async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, readStatus: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, []);

  // Get filtered navigation based on user role
  const filteredNavigation = useMemo(() => {
    if (!currentUser) return [];

    // Individual (B2C) teacher → ONLY one nav item: "Teaching" linking to AI Teacher Content Engine
    const isIndividual = currentUser.role === 'teacher' && !currentUser.schoolId;
    if (isIndividual) {
      return [
        { id: 'learning', label: 'Teaching', path: '/teacher-assignments', roles: ['teacher'] },
      ];
    }

    return navigationItems
      .filter(item => item.roles.includes(currentUser.role))
      .map(item => {
        // Teacher sees "Teaching" instead of "Learning"
        if (item.id === 'learning' && currentUser.role === 'teacher') {
          return { ...item, label: 'Teaching' };
        }
        return item;
      });
  }, [currentUser]);

  // Check if user has specific role
  const hasRole = useCallback((roles) => {
    if (!currentUser) return false;
    const roleList = Array.isArray(roles) ? roles : [roles];
    return roleList.includes(currentUser.role);
  }, [currentUser]);

  // Check if user is admin
  const isAdmin = useMemo(() => {
    return currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  }, [currentUser]);

  // Check if user is super admin
  const isSuperAdmin = useMemo(() => {
    return currentUser?.role === 'super_admin';
  }, [currentUser]);

  const value = {
    // Auth state
    isAuthenticated,
    isLoading,
    currentUser,
    currentSchool,
    
    // Auth actions
    login,
    loginIndividual,
    loginWithGoogleSession,
    logout,
    
    // UI state
    sidebarCollapsed,
    mobileDrawerOpen,
    theme,
    toggleSidebar,
    toggleMobileDrawer,
    closeMobileDrawer,
    toggleTheme,
    
    // Navigation
    filteredNavigation,
    navigationItems,
    
    // Notifications
    notifications,
    unreadCount,
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    
    // Role helpers
    hasRole,
    isAdmin,
    isSuperAdmin,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
